import { useTranslation } from 'react-i18next';
import type { MasterDataRecordDto } from '@complan/contracts';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EditorDialog, EditorDialogActions } from '@/shared/components/EditorDialog';

/**
 * The delete-request dialog (task 23.6, R16.6).
 *
 * A master data record is never hard-deleted. When a delete is requested the API
 * refuses it and reports the exact number of referencing Initiatives, offering
 * deactivation as the alternative. This dialog surfaces that: it states the
 * referencing count (from the record's own `referencingInitiativeCount`, which the
 * list read already carries, and confirmed by the refusal finding) and offers a
 * single action — deactivate the record — which withholds it from new selections
 * while keeping every existing association. The count is shown whether it is zero
 * or more, because the platform holds no delete capability at all.
 */
export function DeleteRecordDialog({
  record,
  open,
  onOpenChange,
  onDeactivate,
  isSubmitting,
  onCloseAutoFocus,
}: {
  readonly record: MasterDataRecordDto | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onDeactivate: () => void;
  readonly isSubmitting: boolean;
  /**
   * Restores keyboard focus to the row's Delete button that opened this dialog
   * (R10.13's general focus-restore expectation, R18.6) — see the equivalent
   * prop on {@link RecordEditorDialog} for why this is needed on a
   * `DialogTrigger`-less controlled `Dialog`.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const count = record?.referencingInitiativeCount ?? 0;

  return (
    <EditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.masterData.deleteDialog.title')}
      width="max-w-[460px]"
      {...(onCloseAutoFocus ? { onCloseAutoFocus } : {})}
    >
      <Alert className="border-[#F0DBB4] bg-[#FDF4E4]">
        <AlertDescription>
          <p className="text-[12px] text-[#7D5300]">
            {count > 0
              ? t('admin.masterData.deleteDialog.referenced', { count })
              : t('admin.masterData.deleteDialog.unreferenced')}
          </p>
        </AlertDescription>
      </Alert>

      <EditorDialogActions>
        <Button
          type="button"
          disabled={isSubmitting || record === null || !record.isActive}
          onClick={onDeactivate}
        >
          {t('admin.masterData.deleteDialog.deactivateAndClose')}
        </Button>
        <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
          {t('admin.masterData.deleteDialog.keep')}
        </Button>
      </EditorDialogActions>
    </EditorDialog>
  );
}
